import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 1000 : -1000,
    opacity: 0
  }),
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    zIndex: 0,
    x: direction < 0 ? 1000 : -1000,
    opacity: 0
  })
};

const swipeConfidenceThreshold = 10000;
const swipePower = (offset: number, velocity: number) => {
  return Math.abs(offset) * velocity;
};

const projects = [
  {
    title: "Pipeline CI/CD Automatisé",
    description: "Pipeline d'intégration et déploiement continu pour applications microservices",
    technologies: ["Jenkins", "Docker", "Kubernetes", "GitLab", "ArgoCD"],
    image: "/images/pipeline.jpg",
    details: [
      "Configuration de pipelines CI/CD automatisés",
      "Intégration avec Kubernetes et ArgoCD",
      "Gestion des secrets et de la sécurité",
      "Monitoring et alerting"
    ],
    link: "https://gitlab.rca.mg"
  },
  {
    title: "Système de Monitoring Cloud",
    description: "Plateforme de surveillance cloud avec alerting en temps réel",
    technologies: ["AWS", "Terraform", "Grafana", "Prometheus", "Docker"],
    image: "/images/monitoring-dashboard.jpg",
    details: [
      "Mise en place d'une infrastructure de monitoring complète sur AWS",
      "Intégration de Prometheus et Grafana pour la visualisation des métriques",
      "Développement d'alertes personnalisées basées sur des seuils dynamiques",
      "Réduction de 80% du temps de détection des incidents"
    ],
    link: "https://grafana.rca.mg"
  },
  {
    title: "Sécurité Cloud Native",
    description: "Solution de sécurité pour environnements cloud natifs",
    technologies: ["Kubernetes", "OPA", "Falco", "Trivy", "Terraform"],
    image: "/images/cloud-security.jpg",
    details: [
      "Implémentation de politiques de sécurité as code",
      "Mise en place de scan de vulnérabilités automatisé",
      "Intégration avec les outils de SIEM",
      "Conformité automatisée avec les standards CIS"
    ],
    link: "https://ids.yif.io"
  }
];

const Projects: React.FC = () => {
  const [[page, direction], setPage] = useState([0, 0]);
  const [isAnimating, setIsAnimating] = useState(false);

  const paginate = useCallback((newDirection: number) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setPage(([currentPage]) => [
      currentPage + newDirection < 0 
        ? projects.length - 1 
        : currentPage + newDirection >= projects.length 
        ? 0 
        : currentPage + newDirection,
      newDirection
    ]);
  }, [isAnimating]);

  useEffect(() => {
    const autoSlide = () => {
      paginate(1);
    };
    const timer = setInterval(autoSlide, 8000);
    return () => clearInterval(timer);
  }, [paginate]);

  return (
    <section id="projects" className="py-20 bg-gradient-to-b from-base-300 to-base-200">
      <div className="container mx-auto px-4">
        <h2 className="text-4xl font-bold text-center mb-16">Mes Projets</h2>
        
        <div className="relative h-[600px] max-w-6xl mx-auto">
          <AnimatePresence initial={false} custom={direction} mode="wait" onExitComplete={() => setIsAnimating(false)}>
            <motion.div
              key={page}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 }
              }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={1}
              onDragEnd={(e, { offset, velocity }) => {
                const swipe = swipePower(offset.x, velocity.x);
                if (swipe < -swipeConfidenceThreshold) {
                  paginate(1);
                } else if (swipe > swipeConfidenceThreshold) {
                  paginate(-1);
                }
              }}
              className="absolute w-full"
            >
              <div className="bg-base-100 rounded-2xl shadow-2xl overflow-hidden mx-4">
                <div className="grid lg:grid-cols-2 gap-0">
                  <div className="relative h-[300px] lg:h-[600px] bg-base-100">
                    <div className="absolute inset-0 flex items-center justify-center p-8 z-20">
                      <h3 className="text-4xl font-bold text-primary">{projects[page].title}</h3>
                    </div>
                  </div>
                  
                  <div className="p-8 lg:p-12 flex flex-col justify-between bg-base-200">
                    <div>
                      <p className="text-lg mb-8 text-base-content/80">
                        {projects[page].description}
                      </p>
                      
                      <div className="space-y-3 mb-8">
                        {projects[page].details.map((detail, index) => (
                          <div key={index} className="flex items-start gap-3">
                            <span className="text-primary mt-1">•</span>
                            <p className="text-base-content/70">{detail}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex flex-wrap gap-2 mb-8">
                        {projects[page].technologies.map((tech) => (
                          <span
                            key={tech}
                            className="px-4 py-2 rounded-full bg-base-300 text-sm font-medium text-base-content/70 hover:bg-primary/20 transition-colors"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                      
                      {projects[page].link && (
                        <a
                          href={projects[page].link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-content hover:bg-primary-focus transition-colors"
                        >
                          Voir le projet
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                          </svg>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 btn btn-circle btn-ghost bg-base-200/50 hover:bg-base-200"
            onClick={() => paginate(-1)}
          >
            <ChevronLeftIcon className="w-6 h-6" />
          </button>

          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 btn btn-circle btn-ghost bg-base-200/50 hover:bg-base-200"
            onClick={() => paginate(1)}
          >
            <ChevronRightIcon className="w-6 h-6" />
          </button>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {projects.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  const newDirection = index > page ? 1 : -1;
                  setPage([index, newDirection]);
                }}
                className={`w-3 h-3 rounded-full transition-colors duration-200 ${
                  index === page ? 'bg-primary' : 'bg-base-300'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;
